import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { Badge } from './badge.js';
import { Button } from './button.js';
import { Input } from './input.js';

export interface TagInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'disabled'
> {
  value: string[];
  onChange: (tags: string[]) => void;
  normalizeTag?: (raw: string) => string;
  removeLabel: (tag: string) => string;
  disabled?: boolean;
  invalid?: boolean;
}

const COMMIT_KEYS = new Set(['Enter', ',']);

const TagInput = React.forwardRef<HTMLInputElement, TagInputProps>(
  (
    {
      className,
      value,
      onChange,
      normalizeTag = (raw) => raw.trim(),
      removeLabel,
      disabled = false,
      invalid,
      onKeyDown,
      onBlur,
      onPaste,
      ...inputProps
    },
    ref,
  ) => {
    const [draft, setDraft] = React.useState('');
    const inputRef = React.useRef<HTMLInputElement>(null);
    const shouldRefocusInputRef = React.useRef(false);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    // Removing a chip unmounts the focused button; focus goes back to the input after the commit.
    React.useEffect(() => {
      if (!shouldRefocusInputRef.current) return;
      shouldRefocusInputRef.current = false;
      inputRef.current?.focus();
    }, [value]);

    // Split on commas, so a pasted list becomes the same tags as typing it would. Newlines
    // never reach the draft: a text input strips them, so `onPaste` turns them into commas.
    const withDraft = (tags: string[]) => {
      const draftTags = draft
        .split(',')
        .map((part) => normalizeTag(part))
        .filter((tag) => tag.length > 0 && !tags.includes(tag));
      return draftTags.length === 0 ? tags : [...tags, ...new Set(draftTags)];
    };

    const commitDraft = () => {
      const next = withDraft(value);
      setDraft('');
      if (next !== value) onChange(next);
    };

    // One change covers the pending text and the removal: two onChange calls from the same
    // render would each start from the same stale `value`, and the second would drop the first.
    // Removal is by position: `value` may hold duplicates, and only the clicked chip should go.
    const removeTag = (index: number) => {
      shouldRefocusInputRef.current = true;
      setDraft('');
      onChange(withDraft(value).filter((_, existingIndex) => existingIndex !== index));
    };

    return (
      <div className={cn('space-y-3', className)}>
        {value.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {value.map((tag, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="gap-1 pr-1 font-normal"
                data-testid="tag-input-tag"
              >
                {tag}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 rounded-full"
                  aria-label={removeLabel(tag)}
                  disabled={disabled}
                  // Keeps focus in the text input, so its blur commit does not run ahead of the removal.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => removeTag(index)}
                >
                  <X className="h-3 w-3" aria-hidden />
                </Button>
              </Badge>
            ))}
          </div>
        )}
        <Input
          ref={inputRef}
          type="text"
          className={cn(invalid && 'border-destructive')}
          value={draft}
          disabled={disabled}
          aria-invalid={invalid ?? undefined}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            onKeyDown?.(event);
            if (
              event.defaultPrevented ||
              !COMMIT_KEYS.has(event.key) ||
              event.nativeEvent.isComposing
            ) {
              return;
            }
            event.preventDefault();
            commitDraft();
          }}
          onPaste={(event) => {
            onPaste?.(event);
            if (event.defaultPrevented) return;
            const text = event.clipboardData.getData('text');
            if (!/[\r\n]/.test(text)) return;
            event.preventDefault();
            const { value: current, selectionStart, selectionEnd } = event.currentTarget;
            const start = selectionStart ?? current.length;
            const end = selectionEnd ?? start;
            setDraft(current.slice(0, start) + text.replace(/\r?\n/g, ',') + current.slice(end));
          }}
          onBlur={(event) => {
            commitDraft();
            onBlur?.(event);
          }}
          {...inputProps}
        />
      </div>
    );
  },
);
TagInput.displayName = 'TagInput';

export { TagInput };
